import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Plus,
  Search,
  Image,
  FileText,
  Video,
  Calendar,
  Grid,
  List,
  ExternalLink,
  Download,
  Eye,
  X,
  Upload,
  Edit,
  Trash2,
} from "lucide-react";

const Comunicacion = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("assets");
  const [resources, setResources] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [showModal, setShowModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [newResource, setNewResource] = useState({
    title: "",
    description: "",
    url: "",
    type: "image",
    tags: [],
  });
  const [newPost, setNewPost] = useState({
    title: "",
    platform: "Instagram",
    date: "",
    time: "",
    content: "",
    status: "draft",
  });

  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";

  const fetchResources = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/resources?module=comunicacion`, {
        withCredentials: true,
      });
      setResources(response.data || []);
    } catch (error) {
      console.error("Error fetching resources:", error);
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchScheduledPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/scheduled-posts`, {
        withCredentials: true,
      });
      setScheduledPosts(response.data || []);
    } catch (error) {
      // Silently handle 404 errors as endpoints may not be implemented yet
      if (error.response?.status !== 404) {
        console.error("Error fetching scheduled posts:", error);
      }
      setScheduledPosts([]);
    }
  }, []);

  useEffect(() => {
    fetchResources();
    fetchScheduledPosts();
  }, [fetchResources, fetchScheduledPosts]);

  const handleCreateResource = async () => {
    if (!newResource.title.trim()) return;
    try {
      const response = await axios.post(
        `${API}/resources`,
        { ...newResource, module: "comunicacion" },
        { withCredentials: true }
      );
      setResources([...resources, response.data]);
    } catch (error) {
      console.error("Error creating resource:", error);
      const mockResource = { resource_id: `res_${Date.now()}`, ...newResource };
      setResources([...resources, mockResource]);
    }
    setShowModal(false);
    setEditingResource(null);
    setNewResource({
      title: "",
      description: "",
      url: "",
      type: "image",
      tags: [],
    });
  };

  const handleEditResource = (resource) => {
    setEditingResource(resource);
    setNewResource({
      title: resource.title,
      description: resource.description || "",
      url: resource.url || "",
      type: resource.type || "image",
      tags: resource.tags || [],
    });
    setShowModal(true);
  };

  const handleUpdateResource = async () => {
    if (!editingResource) return;
    try {
      await axios.put(`${API}/resources/${editingResource.resource_id || editingResource.id}`, newResource, {
        withCredentials: true,
      });
    } catch (error) {
      console.error("Error updating resource:", error);
    }
    setResources(resources.map(r => 
      (r.resource_id || r.id) === (editingResource.resource_id || editingResource.id)
        ? { ...r, ...newResource }
        : r
    ));
    setShowModal(false);
    setEditingResource(null);
    setNewResource({
      title: "",
      description: "",
      url: "",
      type: "image",
      tags: [],
    });
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm("¿Estás seguro de eliminar este asset?")) return;
    try {
      await axios.delete(`${API}/resources/${resourceId}`, { withCredentials: true });
    } catch (error) {
      console.error("Error deleting resource:", error);
    }
    setResources(resources.filter(r => (r.resource_id || r.id) !== resourceId));
    if (selectedResource && (selectedResource.resource_id || selectedResource.id) === resourceId) {
      setSelectedResource(null);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.title.trim() || !newPost.date) return;
    try {
      const response = await axios.post(
        `${API}/scheduled-posts`,
        newPost,
        { withCredentials: true }
      );
      setScheduledPosts([...scheduledPosts, response.data]);
    } catch (error) {
      console.error("Error creating post:", error);
      const mockPost = { post_id: `post_${Date.now()}`, ...newPost };
      setScheduledPosts([...scheduledPosts, mockPost]);
    }
    setShowPostModal(false);
    setEditingPost(null);
    setNewPost({
      title: "",
      platform: "Instagram",
      date: "",
      time: "",
      content: "",
      status: "draft",
    });
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setNewPost({
      title: post.title || "",
      platform: post.platform || "Instagram",
      date: post.date || "",
      time: post.time || "",
      content: post.content || "",
      status: post.status || "draft",
    });
    setShowPostModal(true);
  };

  const handleUpdatePost = async () => {
    if (!editingPost) return;
    try {
      await axios.put(
        `${API}/scheduled-posts/${editingPost.post_id || editingPost.id}`,
        newPost,
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error updating post:", error);
    }
    setScheduledPosts(scheduledPosts.map(p => 
      (p.post_id || p.id) === (editingPost.post_id || editingPost.id)
        ? { ...p, ...newPost }
        : p
    ));
    setShowPostModal(false);
    setEditingPost(null);
    setNewPost({
      title: "",
      platform: "Instagram",
      date: "",
      time: "",
      content: "",
      status: "draft",
    });
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta publicación?")) return;
    try {
      await axios.delete(`${API}/scheduled-posts/${postId}`, { withCredentials: true });
    } catch (error) {
      console.error("Error deleting post:", error);
    }
    setScheduledPosts(scheduledPosts.filter(p => (p.post_id || p.id) !== postId));
  };

  const getResourceIcon = (type) => {
    const icons = {
      image: Image,
      drive_pdf: FileText,
      drive_doc: FileText,
      youtube: Video,
    };
    return icons[type] || FileText;
  };

  const getPlatformColor = (platform) => {
    const colors = {
      Instagram: "bg-gradient-to-r from-purple-500 to-pink-500",
      LinkedIn: "bg-blue-600",
      Twitter: "bg-sky-500",
      Facebook: "bg-blue-700",
    };
    return colors[platform] || "bg-slate-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="comunicacion">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Comunicación</h1>
          <p className="text-slate-500">Galería de assets y calendario de publicaciones</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass-panel p-1 inline-flex rounded-full">
        {[
          { id: "assets", label: "Galería de Assets", icon: Image },
          { id: "calendario", label: "Calendario", icon: Calendar },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
              activeTab === tab.id
                ? "bg-primary text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Assets Tab */}
      {activeTab === "assets" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar assets..."
                className="w-full glass-input pl-10"
                data-testid="search-assets-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center glass-panel p-1 rounded-xl">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === "grid" ? "bg-primary text-white" : "text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  <Grid size={18} />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === "list" ? "bg-primary text-white" : "text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  <List size={18} />
                </button>
              </div>
              <button
                onClick={() => {
                  setEditingResource(null);
                  setNewResource({
                    title: "",
                    description: "",
                    url: "",
                    type: "image",
                    tags: [],
                  });
                  setShowModal(true);
                }}
                className="btn-primary flex items-center gap-2"
                data-testid="add-asset-btn"
              >
                <Upload size={20} />
                Subir Asset
              </button>
            </div>
          </div>

          {viewMode === "grid" ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {resources.length > 0 ? (
                resources.map((asset) => (
                  <div
                    key={asset.id || asset.resource_id}
                    className="glass-panel overflow-hidden card-hover cursor-pointer group"
                    onClick={() => setSelectedResource(asset)}
                    data-testid={`asset-${asset.id || asset.resource_id}`}
                  >
                    {asset.type === "image" ? (
                      <div className="aspect-square bg-slate-100 relative">
                        <img
                          src={asset.url}
                          alt={asset.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Eye size={24} className="text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-square bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center relative">
                        {(() => {
                          const Icon = getResourceIcon(asset.type);
                          return <Icon size={48} className="text-primary" />;
                        })()}
                        <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Eye size={24} className="text-white" />
                        </div>
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-medium text-slate-800 truncate">{asset.title}</h3>
                      <div className="flex gap-1 mt-2">
                        {asset.tags?.slice(0, 2).map((tag, i) => (
                          <span key={i} className="badge bg-slate-100 text-slate-600 text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      {isSuperAdmin && (
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditResource(asset); }}
                            className="flex-1 btn-secondary py-1.5 text-xs flex items-center justify-center gap-1"
                          >
                            <Edit size={12} />
                            Editar
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteResource(asset.resource_id || asset.id); }}
                            className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full glass-panel p-12 text-center">
                  <Image size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay assets disponibles</p>
                </div>
              )}
            </div>
          ) : (
            <div className="glass-panel divide-y divide-slate-100">
              {resources.length > 0 ? (
                resources.map((asset) => {
                  const Icon = getResourceIcon(asset.type);
                  return (
                    <div
                      key={asset.id || asset.resource_id}
                      className="flex items-center gap-4 p-4 hover:bg-slate-50/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedResource(asset)}
                    >
                      <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                        {asset.type === "image" ? (
                          <img src={asset.url} alt={asset.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                            <Icon size={24} className="text-primary" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-800">{asset.title}</h3>
                        <div className="flex gap-2 mt-1">
                          {asset.tags?.map((tag, i) => (
                            <span key={i} className="badge bg-slate-100 text-slate-600 text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isSuperAdmin && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEditResource(asset); }}
                              className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteResource(asset.resource_id || asset.id); }}
                              className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                        <a
                          href={asset.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={18} className="text-slate-500" />
                        </a>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-12 text-center">
                  <Image size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay assets disponibles</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Calendario Tab */}
      {activeTab === "calendario" && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={() => {
                setEditingPost(null);
                setNewPost({
                  title: "",
                  platform: "Instagram",
                  date: "",
                  time: "",
                  content: "",
                  status: "draft",
                });
                setShowPostModal(true);
              }}
              className="btn-primary flex items-center gap-2"
              data-testid="schedule-post-btn"
            >
              <Plus size={20} />
              Programar Publicación
            </button>
          </div>

          {scheduledPosts.length > 0 ? (
            <div className="glass-panel divide-y divide-slate-100">
              {scheduledPosts.map((post) => (
                <div
                  key={post.id || post.post_id}
                  className="flex items-center gap-4 p-6 hover:bg-slate-50/50 transition-colors"
                  data-testid={`post-${post.id || post.post_id}`}
                >
                  <div className={`w-12 h-12 rounded-xl ${getPlatformColor(post.platform)} flex items-center justify-center text-white font-bold`}>
                    {post.platform.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-slate-800">{post.title}</h3>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-sm text-slate-500">{post.platform}</span>
                      <span className="text-sm text-slate-400">
                        {post.date ? new Date(post.date).toLocaleDateString("es-ES", {
                          day: "numeric",
                          month: "long",
                        }) : "Sin fecha"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`badge ${
                      post.status === "scheduled" ? "badge-success" : "badge-warning"
                    }`}>
                      {post.status === "scheduled" ? "Programado" : "Borrador"}
                    </span>
                    {isSuperAdmin && (
                      <>
                        <button
                          onClick={() => handleEditPost(post)}
                          className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeletePost(post.post_id || post.id)}
                          className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="glass-panel p-12 text-center">
              <Calendar size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay publicaciones programadas</p>
            </div>
          )}
        </div>
      )}

      {/* Asset Preview Modal */}
      {selectedResource && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-4 w-full max-w-4xl max-h-[90vh] overflow-hidden animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-800">{selectedResource.title}</h2>
              <div className="flex items-center gap-2">
                {isSuperAdmin && (
                  <>
                    <button
                      onClick={() => {
                        setSelectedResource(null);
                        handleEditResource(selectedResource);
                      }}
                      className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => {
                        handleDeleteResource(selectedResource.resource_id || selectedResource.id);
                      }}
                      className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setSelectedResource(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="aspect-video bg-slate-100 rounded-2xl overflow-hidden">
              {selectedResource.type === "image" ? (
                <img
                  src={selectedResource.url}
                  alt={selectedResource.title}
                  className="w-full h-full object-contain"
                />
              ) : selectedResource.url.includes("docs.google.com") ? (
                <iframe
                  src={`${selectedResource.url.replace("/edit", "/preview")}`}
                  className="w-full h-full"
                  title={selectedResource.title}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <a
                    href={selectedResource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary flex items-center gap-2"
                  >
                    <ExternalLink size={20} />
                    Abrir en nueva pestaña
                  </a>
                </div>
              )}
            </div>
            
            <div className="mt-4 flex items-center justify-between">
              <div className="flex gap-2">
                {selectedResource.tags?.map((tag, i) => (
                  <span key={i} className="badge bg-slate-100 text-slate-600">
                    {tag}
                  </span>
                ))}
              </div>
              <a
                href={selectedResource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary flex items-center gap-2"
              >
                <Download size={18} />
                Descargar
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Add Asset Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-8 w-full max-w-lg animate-slide-up" data-testid="add-asset-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">
                {editingResource ? "Editar Asset" : "Agregar Asset"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingResource(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Título</label>
                <input
                  type="text"
                  value={newResource.title}
                  onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
                  className="w-full glass-input"
                  placeholder="Nombre del asset"
                  data-testid="asset-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo</label>
                <select
                  value={newResource.type}
                  onChange={(e) => setNewResource({ ...newResource, type: e.target.value })}
                  className="w-full glass-input"
                  data-testid="asset-type-select"
                >
                  <option value="image">Imagen</option>
                  <option value="drive_pdf">PDF (Google Drive)</option>
                  <option value="drive_doc">Documento (Google Drive)</option>
                  <option value="youtube">Video (YouTube)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">URL</label>
                <input
                  type="url"
                  value={newResource.url}
                  onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
                  className="w-full glass-input"
                  placeholder="https://..."
                  data-testid="asset-url-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                <textarea
                  value={newResource.description}
                  onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
                  className="w-full glass-input min-h-[80px]"
                  placeholder="Descripción del asset"
                  data-testid="asset-description-input"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button 
                  onClick={() => {
                    setShowModal(false);
                    setEditingResource(null);
                  }} 
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button 
                  onClick={editingResource ? handleUpdateResource : handleCreateResource} 
                  className="btn-primary" 
                  data-testid="save-asset-btn"
                >
                  {editingResource ? "Guardar Cambios" : "Agregar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Post Modal */}
      {showPostModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-8 w-full max-w-lg animate-slide-up" data-testid="schedule-post-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">
                {editingPost ? "Editar Publicación" : "Programar Publicación"}
              </h2>
              <button
                onClick={() => {
                  setShowPostModal(false);
                  setEditingPost(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Título</label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  className="w-full glass-input"
                  placeholder="Título de la publicación"
                  data-testid="post-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Plataforma</label>
                  <select
                    value={newPost.platform}
                    onChange={(e) => setNewPost({ ...newPost, platform: e.target.value })}
                    className="w-full glass-input"
                    data-testid="post-platform-select"
                  >
                    <option value="Instagram">Instagram</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Twitter">Twitter</option>
                    <option value="Facebook">Facebook</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Estado</label>
                  <select
                    value={newPost.status}
                    onChange={(e) => setNewPost({ ...newPost, status: e.target.value })}
                    className="w-full glass-input"
                    data-testid="post-status-select"
                  >
                    <option value="draft">Borrador</option>
                    <option value="scheduled">Programado</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Fecha</label>
                  <input
                    type="date"
                    value={newPost.date}
                    onChange={(e) => setNewPost({ ...newPost, date: e.target.value })}
                    className="w-full glass-input"
                    data-testid="post-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Hora</label>
                  <input
                    type="time"
                    value={newPost.time}
                    onChange={(e) => setNewPost({ ...newPost, time: e.target.value })}
                    className="w-full glass-input"
                    data-testid="post-time-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Contenido</label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  className="w-full glass-input min-h-[120px]"
                  placeholder="Escribe el contenido de la publicación..."
                  data-testid="post-content-input"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowPostModal(false);
                    setEditingPost(null);
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={editingPost ? handleUpdatePost : handleCreatePost}
                  disabled={!newPost.title.trim() || !newPost.date}
                  className="btn-primary"
                  data-testid="save-post-btn"
                >
                  {editingPost ? "Guardar Cambios" : "Programar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Comunicacion;
